import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";
import { useGetLocationById } from "@/locations/api/queries/location.queries.ts";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries.ts";
import { MACHINES_URL } from "@/machines/routes.ts";

export const useExportBreadcrumbs = () => {
  const { t } = useTranslation("export");
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get("location");
  const machineParam = searchParams.get("machine");

  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgsData, isLoading: orgsLoading } = useGetOrganizations();
  const locationId = locationParam ? parseInt(locationParam) : undefined;
  const { data: locationData, isLoading: isLocationLoading } = useGetLocationById(locationId as number);
  const { data: machinesData, isLoading: machinesLoading } = useGetMachinesByLocation(locationId);

  const organization = orgsData?.data?.find(org => org.id === selectedOrganizationId);
  const { machineName, machineId } = useMemo(() => {
    if (!machineParam || !machinesData?.data) return { machineName: null as string | null, machineId: undefined as number | undefined };
    const m = machinesData.data.find(m => m.device_mxid === machineParam);
    return { machineName: m?.name ?? null, machineId: m?.id };
  }, [machineParam, machinesData]);

  const crumbs = useMemo(() => {
    const list: Array<any> = [
      {
        path: "/",
        breadcrumb: organization?.name || t("breadcrumbs.organization"),
        type: "organizations" as const,
        currentId: selectedOrganizationId!,
        isLoading: orgsLoading,
      },
    ];

    if (locationParam) {
      list.push({
        path: MACHINES_URL.LOCATION_DETAIL.replace(":id", String(locationParam)),
        breadcrumb: locationData?.data?.name || t("breadcrumbs.location", { id: locationParam }),
        type: "locations" as const,
        currentId: locationId,
        isLoading: isLocationLoading,
      });
    }

    if (machineParam) {
      list.push({
        path: MACHINES_URL.MACHINE_DETAIL.replace(":id", String(machineId ?? "")),
        breadcrumb: machineName || t("breadcrumbs.machine"),
        type: "machines" as const,
        currentId: machineId,
        locationId: locationId,
        isLoading: machinesLoading,
      });
    }

    list.push({ path: MACHINES_URL.EXPORT, breadcrumb: t("breadcrumbs.export") });
    return list;
  }, [organization?.name, selectedOrganizationId, orgsLoading, locationParam, locationData?.data?.name, locationId, isLocationLoading, machineParam, machineName, machinesLoading, t]);

  return crumbs;
};


